/**
 * useDeviceLink — one state machine for the app↔wearable console link.
 *
 * WHY THIS EXISTS
 * The ops surface previously tracked the link with a handful of independent
 * booleans (waking, consoleReady, connecting, plus a mode flag per flow). Any
 * combination was reachable, nothing owned "what is happening right now", and
 * two of those combinations were dead ends a user could not leave:
 *
 *   1. The connect handshake gave up silently after 15 s ("give up quietly;
 *      user can retry"). Commands stayed locked, and the safety-net status
 *      poll was gated behind having already seen a status reply -- so it never
 *      started either. The dashboard sat there, blank and inert, with no
 *      indication that anything could be done about it.
 *   2. An activity that owned the stream (export, erase, a flow) had no
 *      deadline. If it never resolved, the poll stayed torn down and the whole
 *      surface stayed stale with no way back short of closing the modal.
 *
 * The fix is structural, not another flag: ONE phase for the link, ONE slot
 * for whatever currently owns the stream, and the rule that neither can ever
 * be terminal. `unresponsive` keeps retrying with backoff and always offers a
 * visible action. Every activity carries a deadline.
 *
 * The decision logic below is pure and exported so it can be tested without a
 * device, a port, or fake timers. The hook is a thin shell over it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { t } from "i18n";

/* ------------------------------------------------------------------ */
/* Link phases                                                         */
/* ------------------------------------------------------------------ */
/* Deliberately NOT a boolean pair. "connected" (the port is open) and
 * "responsive" (the console answers) are different facts, and conflating them
 * is what produced a dashboard that looked connected while being deaf. */
export const LINK = {
  DISCONNECTED: "disconnected", // no port
  CONNECTING: "connecting", // opening the port
  HANDSHAKING: "handshaking", // port open, chasing the console awake
  READY: "ready", // console answering
  UNRESPONSIVE: "unresponsive", // handshake exhausted -- STILL RETRYING
};

/* What currently owns the serial stream. At most one, because the wire is
 * exclusive: a status poll landing mid-export corrupts the frame parser. */
export const ACTIVITY = {
  COMMAND: "command",
  CONFIG: "config",
  EXPORT: "export",
  ERASE: "erase",
  FLOW: "flow",
};

/* Poll cadences. The pushed NDJSON events (state/substate/impact/store) remain
 * the primary live source; this is the floor under them.
 *
 * The old single 12 s cadence was wrong in both directions: too slow to feel
 * responsive right after you change something, and too slow to notice a dead
 * event stream. Splitting it by situation fixes both without adding traffic
 * when the device is quiet and the stream is healthy. */
export const FAST_POLL_MS = 1500; // just changed something -- settle quickly
export const ACTIVE_POLL_MS = 3000; // event stream looks dead -- distrust it
export const IDLE_POLL_MS = 8000; // quiet and healthy -- stay out of the way
export const SETTLE_WINDOW_MS = 6000; // how long a change keeps us in FAST
export const EVENT_STALE_MS = 20000; // no pushed event this long => unhealthy

/* Handshake: one attempt window, then retry forever with backoff. The cap
 * matters more than the curve -- an operator who walks away and comes back
 * should find a live dashboard, not a stalled one. */
export const HANDSHAKE_WINDOW_MS = 15000;
export const RETRY_BACKOFF_MS = [2000, 5000, 10000, 15000];

/* Activities must finish. These are backstops for a promise that never
 * settles, not expected timings -- an export of a full store legitimately
 * takes minutes, so the ceiling is generous and only catches true hangs. */
export const ACTIVITY_TIMEOUT_MS = {
  [ACTIVITY.COMMAND]: 30000,
  [ACTIVITY.CONFIG]: 30000,
  [ACTIVITY.ERASE]: 120000,
  [ACTIVITY.EXPORT]: 600000,
  [ACTIVITY.FLOW]: 0, // 0 = no deadline; flows are user-driven and can idle
};

/* ------------------------------------------------------------------ */
/* Pure decisions                                                      */
/* ------------------------------------------------------------------ */

/**
 * The link phase, derived from facts rather than stored as a flag.
 * Order matters: port state dominates, then console liveness.
 */
export function linkPhase({
  connected,
  connecting,
  consoleAlive,
  handshakeExhausted,
}) {
  if (connecting) return LINK.CONNECTING;
  if (!connected) return LINK.DISCONNECTED;
  if (consoleAlive) return LINK.READY;
  return handshakeExhausted ? LINK.UNRESPONSIVE : LINK.HANDSHAKING;
}

/**
 * How often to poll `status`, in ms. null means "do not poll".
 *
 * The ONLY null cases are: the link cannot carry a poll, or something else
 * owns the stream. Critically there is no "we never saw a status so we will
 * never ask for one" case -- that gate is what made the old dead end
 * unrecoverable.
 */
export function pollIntervalMs({
  phase,
  activity,
  msSinceChange = Infinity,
  msSinceEvent = Infinity,
}) {
  if (phase !== LINK.READY) return null;
  if (activity) return null;
  if (msSinceChange < SETTLE_WINDOW_MS) return FAST_POLL_MS;
  return msSinceEvent > EVENT_STALE_MS ? ACTIVE_POLL_MS : IDLE_POLL_MS;
}

/** Can the user issue a command right now? */
export function canSendCommands({ phase, activity }) {
  return phase === LINK.READY && !activity;
}

/**
 * What the UI should offer the user, given the phase. Never returns null for a
 * stuck phase: every non-ready state has a way forward the user can see and
 * act on. That is the whole point of the rewrite.
 */
export function recoveryAction({ phase, activity, retryCount = 0 }) {
  switch (phase) {
    case LINK.DISCONNECTED:
      return {
        kind: "connect",
        label: t("utils.deviceLink.connect"),
        tone: "primary",
      };
    case LINK.CONNECTING:
      return {
        kind: "wait",
        label: t("utils.deviceLink.connecting"),
        tone: "muted",
        busy: true,
      };
    case LINK.HANDSHAKING:
      return {
        kind: "wait",
        label: t("utils.deviceLink.wakingConsole"),
        tone: "muted",
        busy: true,
      };
    case LINK.UNRESPONSIVE:
      return {
        kind: "retry",
        label:
          retryCount > 2
            ? t("utils.deviceLink.stillRetrying")
            : t("utils.deviceLink.retryNow"),
        tone: "warn",
        // Retrying in the background regardless; the button just skips the wait.
        detail: t("utils.deviceLink.unresponsiveDetail"),
      };
    case LINK.READY:
      return activity
        ? {
            kind: "cancel",
            label: t("common.actions.cancel"),
            tone: "muted",
            busy: true,
          }
        : null;
    default:
      return null;
  }
}

/** Backoff for retry attempt N (0-based), capped rather than unbounded. */
export function retryDelayMs(attempt) {
  const i = Math.min(attempt, RETRY_BACKOFF_MS.length - 1);
  return RETRY_BACKOFF_MS[Math.max(0, i)];
}

/* ------------------------------------------------------------------ */
/* Hook                                                                */
/* ------------------------------------------------------------------ */

/**
 * @param {object} o
 * @param {boolean} o.connected     port open (from useSerialPort)
 * @param {boolean} o.connecting    port opening
 * @param {function} o.wake         async: chase the console awake (send/kick)
 * @param {function} o.poll         fire-and-forget `status`
 * @param {number}  o.lastEventAt   performance.now() of the last pushed event
 * @param {number}  o.lastStatusAt  performance.now() of the last status reply
 */
export function useDeviceLink({
  connected,
  connecting,
  wake,
  poll,
  lastEventAt = 0,
  lastStatusAt = 0,
}) {
  const [consoleAlive, setConsoleAlive] = useState(false);
  const [handshakeExhausted, setHandshakeExhausted] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [activity, setActivity] = useState(null);
  const [changeAt, setChangeAt] = useState(0);

  const activityRef = useRef(null);
  const aliveRef = useRef(false);
  aliveRef.current = consoleAlive;

  const phase = linkPhase({
    connected,
    connecting,
    consoleAlive,
    handshakeExhausted,
  });

  /* A status reply is the definition of "alive". Any reply clears the
   * exhausted flag too -- recovery is automatic, never something the user has
   * to notice and act on. */
  useEffect(() => {
    if (!lastStatusAt) return;
    setConsoleAlive(true);
    setHandshakeExhausted(false);
    setRetryCount(0);
  }, [lastStatusAt]);

  /* Losing the port resets everything. Without this, reconnecting a device
   * inherited the previous session's exhausted flag and went straight back to
   * unresponsive without trying. */
  useEffect(() => {
    if (!connected) {
      setConsoleAlive(false);
      setHandshakeExhausted(false);
      setRetryCount(0);
      setActivity(null);
      activityRef.current = null;
    }
  }, [connected]);

  /* Handshake, and then keep handshaking. The old version ran once and gave
   * up; this one backs off but never stops, so a device that wakes late (or is
   * replugged) is picked up without the user doing anything. */
  useEffect(() => {
    if (!connected || consoleAlive) return undefined;
    let cancelled = false;
    let timer = null;

    const attempt = async (n) => {
      if (cancelled) return;
      const deadline = performance.now() + HANDSHAKE_WINDOW_MS;
      while (!cancelled && !aliveRef.current && performance.now() < deadline) {
        try {
          await wake();
        } catch {
          /* the console may be deaf; that is what the retry is for */
        }
        await new Promise((r) => {
          timer = setTimeout(r, 1800);
        });
      }
      if (cancelled || aliveRef.current) return;
      setHandshakeExhausted(true);
      setRetryCount(n + 1);
      await new Promise((r) => {
        timer = setTimeout(r, retryDelayMs(n));
      });
      if (!cancelled && !aliveRef.current) attempt(n + 1);
    };

    attempt(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [connected, consoleAlive, wake]);

  /* Adaptive status poll. Runs whenever the link can carry it and nothing owns
   * the stream -- no "have we seen a status yet" precondition. */
  const interval = pollIntervalMs({
    phase,
    activity,
    msSinceChange: changeAt ? performance.now() - changeAt : Infinity,
    msSinceEvent: lastEventAt ? performance.now() - lastEventAt : Infinity,
  });

  useEffect(() => {
    if (interval == null) return undefined;
    const id = setInterval(() => poll(), interval);
    return () => clearInterval(id);
  }, [interval, poll]);

  /* Claim the stream. Returns a release fn; the deadline is a backstop against
   * a promise that never settles, which previously stranded the whole UI. */
  const beginActivity = useCallback((kind) => {
    setActivity(kind);
    activityRef.current = kind;
    const ms = ACTIVITY_TIMEOUT_MS[kind] ?? 0;
    let timer = null;
    if (ms > 0) {
      timer = setTimeout(() => {
        if (activityRef.current === kind) {
          setActivity(null);
          activityRef.current = null;
        }
      }, ms);
    }
    return () => {
      if (timer) clearTimeout(timer);
      if (activityRef.current === kind) {
        setActivity(null);
        activityRef.current = null;
      }
    };
  }, []);

  /* Call after anything that changes device state, so the next few polls are
   * quick and the UI agrees with the device fast. */
  const noteChange = useCallback(() => setChangeAt(performance.now()), []);

  const retryNow = useCallback(() => {
    setHandshakeExhausted(false);
    setConsoleAlive(false);
  }, []);

  return useMemo(
    () => ({
      phase,
      activity,
      isReady: phase === LINK.READY,
      canSend: canSendCommands({ phase, activity }),
      action: recoveryAction({ phase, activity, retryCount }),
      retryCount,
      beginActivity,
      noteChange,
      retryNow,
      pollMs: interval,
    }),
    [phase, activity, retryCount, beginActivity, noteChange, retryNow, interval]
  );
}
